/**
 * FTX 实时行情 WebSocket 客户端
 * 架构: market-service → FastAPI /ftx/market/upstream → ws_manager → Browser
 *
 * 特性:
 *   - 指数退避自动重连 (2s → 4s → 8s → ... → 60s)
 *   - 心跳保活 (10s 发 ping, 30s 无 pong 则重连)
 *   - 连接状态管理 (connecting/connected/reconnecting/disconnected)
 *   - 页面可见性感知 (切回前台时立即检查连接)
 *
 * 用法:
 *   import { connectQuoteWS, disconnectQuoteWS } from '@/utils/ws-quote'
 *   connectQuoteWS({
 *     onQuote: (data) => { ... },
 *     onSnapshot: (data) => { ... },
 *     onConfigUpdate: (data) => { ... }
 *   })
 */

/**
 * WebSocket 连接地址
 * 多环境配置：通过 .env.development / .env.production 切换
 * 开发环境直连后端端口，生产环境通过同源代理
 */
const WS_BASE = (() => {
  const wsHost = process.env.VUE_APP_WS_HOST
  const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:'
  // 开发环境: 直接连接后端 9099 端口
  if (location.hostname === 'localhost' || location.hostname === '127.0.0.1') {
    return `${protocol}//${wsHost || '127.0.0.1'}:9099`
  }
  // 生产环境: 通过同源代理
  return `${protocol}//${location.host}/api`
})()

// ============ 重连配置 ============
const RECONNECT_BASE_DELAY = 2000     // 初始重连延迟 (ms)
const RECONNECT_MAX_DELAY = 60000     // 最大重连延迟 (ms)
const RECONNECT_FACTOR = 2            // 指数退避因子

// ============ 心跳配置 ============
const HEARTBEAT_INTERVAL = 10000      // 心跳发送间隔 (ms)
const PONG_TIMEOUT = 30000            // pong 超时时间 (ms)

// ============ 状态 ============
let ws = null
let reconnectTimer = null
let reconnectDelay = RECONNECT_BASE_DELAY
let callbacks = {}
let subscribedSymbols = []
let heartbeatTimer = null
let pongCheckTimer = null
let lastPongTime = 0
let connectionState = 'disconnected'  // connecting | connected | reconnecting | disconnected
let currentOpts = null                // 保存当前连接选项，重连时复用
let intentionallyClosed = false       // 主动关闭标志，不触发重连

/**
 * 更新连接状态
 */
function setState(state) {
  connectionState = state
  callbacks.onStatus?.(state)
}

/**
 * 连接行情 WebSocket（全品种快照模式）
 * @param {Object} opts
 * @param {Function} opts.onQuote     - 单品种报价回调 (symbol, data)
 * @param {Function} opts.onSnapshot  - 全品种快照回调 (data)
 * @param {Function} opts.onConfigUpdate - 配置变更推送回调 (data)
 * @param {Function} opts.onPriceAlert  - 价格预警触发回调 (data)
 * @param {Function} opts.onPositionClosed - 持仓强平通知回调 (data)
 * @param {Function} opts.onStatus    - 连接状态回调 (status)
 * @param {Array}    opts.symbols     - 要订阅的品种列表 ['XAUUSD','XAGUSD']
 */
export function connectQuoteWS(opts = {}) {
  // 已连接或正在连接则跳过（防止重复调用创建多条 WS 连接）
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return

  callbacks = {
    onQuote: opts.onQuote || (() => {}),
    onSnapshot: opts.onSnapshot || (() => {}),
    onConfigUpdate: opts.onConfigUpdate || (() => {}),
    onPriceAlert: opts.onPriceAlert || (() => {}),
    onPositionClosed: opts.onPositionClosed || (() => {}),
    onStatus: opts.onStatus || (() => {})
  }
  subscribedSymbols = opts.symbols || []
  currentOpts = opts
  intentionallyClosed = false

  _doConnect()
}

function _doConnect() {
  // 清理旧连接和定时器
  _cleanup()

  setState('connecting')

  const url = `${WS_BASE}/ftx/market/ws`
  try {
    ws = new WebSocket(url)
  } catch (e) {
    console.error('[WS] 创建连接失败:', e)
    scheduleReconnect()
    return
  }

  ws.onopen = () => {
    console.log('[WS] ✅ 已连接')
    setState('connected')
    reconnectDelay = RECONNECT_BASE_DELAY  // 连接成功，重置退避
    lastPongTime = Date.now()

    // 订阅指定品种
    subscribedSymbols.forEach(symbol => {
      try {
        ws.send(JSON.stringify({ action: 'subscribe', symbol }))
      } catch (e) {
        console.warn('[WS] 订阅发送失败:', symbol, e)
      }
    })

    // 启动心跳
    startHeartbeat()
  }

  ws.onmessage = (event) => {
    try {
      const msg = JSON.parse(event.data)
      if (msg.type === 'quote') {
        callbacks.onQuote(msg.data?.symbol, msg.data)
      } else if (msg.type === 'snapshot') {
        callbacks.onSnapshot(msg.data)
      } else if (msg.type === 'config_update') {
        callbacks.onConfigUpdate(msg.data)
      } else if (msg.type === 'price_alert') {
        callbacks.onPriceAlert(msg.data)
      } else if (msg.type === 'position_closed') {
        callbacks.onPositionClosed(msg.data)
      } else if (msg.type === 'pong') {
        lastPongTime = Date.now()
      }
    } catch (e) {
      console.warn('[WS] 消息解析失败:', e)
    }
  }

  ws.onclose = (event) => {
    console.log('[WS] 连接关闭:', event.code, event.reason)
    _cleanup()
    setState('disconnected')

    // 非主动关闭时自动重连
    if (!intentionallyClosed) {
      scheduleReconnect()
    }
  }

  ws.onerror = (e) => {
    console.error('[WS] 连接错误:', e)
    setState('reconnecting')
  }
}

/**
 * 安排重连（指数退避）
 */
function scheduleReconnect() {
  if (reconnectTimer) return
  if (intentionallyClosed) return

  setState('reconnecting')
  console.log(`[WS] ${reconnectDelay / 1000}s 后重连...`)

  reconnectTimer = setTimeout(() => {
    reconnectTimer = null
    _doConnect()
    // 指数退避
    reconnectDelay = Math.min(reconnectDelay * RECONNECT_FACTOR, RECONNECT_MAX_DELAY)
  }, reconnectDelay)
}

/**
 * 启动心跳
 */
function startHeartbeat() {
  stopHeartbeat()

  // 定期发送 ping
  heartbeatTimer = setInterval(() => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify({ action: 'ping' }))
      } catch (e) {
        console.warn('[WS] ping 发送失败:', e)
      }
    } else {
      stopHeartbeat()
    }
  }, HEARTBEAT_INTERVAL)

  // 定期检测 pong 超时
  pongCheckTimer = setInterval(() => {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      stopHeartbeat()
      return
    }
    const elapsed = Date.now() - lastPongTime
    if (elapsed > PONG_TIMEOUT) {
      console.warn(`[WS] ⚠️ pong 超时 (${elapsed / 1000}s)，主动断开重连`)
      // 主动断开，触发 onclose → scheduleReconnect
      try { ws.close() } catch (e) { /* close on pong timeout, ignore error */ }
    }
  }, 10000)
}

/**
 * 停止心跳
 */
function stopHeartbeat() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer)
    heartbeatTimer = null
  }
  if (pongCheckTimer) {
    clearInterval(pongCheckTimer)
    pongCheckTimer = null
  }
}

/**
 * 清理连接和定时器
 */
function _cleanup() {
  stopHeartbeat()
  if (ws) {
    // 移除事件监听，防止重复触发
    ws.onopen = null
    ws.onmessage = null
    ws.onclose = null
    ws.onerror = null
    if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
      try { ws.close() } catch (e) { /* close during cleanup, ignore error */ }
    }
    ws = null
  }
}

/**
 * 订阅指定品种的实时报价
 */
export function subscribeSymbol(symbol) {
  if (!subscribedSymbols.includes(symbol)) {
    subscribedSymbols.push(symbol)
  }
  if (ws && ws.readyState === WebSocket.OPEN) {
    try {
      ws.send(JSON.stringify({ action: 'subscribe', symbol }))
    } catch (e) {
      console.warn('[WS] 订阅发送失败:', symbol, e)
    }
  }
}

/**
 * 取消订阅
 */
export function unsubscribeSymbol(symbol) {
  subscribedSymbols = subscribedSymbols.filter(s => s !== symbol)
  if (ws && ws.readyState === WebSocket.OPEN) {
    try {
      ws.send(JSON.stringify({ action: 'unsubscribe', symbol }))
    } catch (e) {
      console.warn('[WS] 取消订阅发送失败:', symbol, e)
    }
  }
}

/**
 * 断开连接
 */
export function disconnectQuoteWS() {
  intentionallyClosed = true
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
  _cleanup()
  setState('disconnected')
  currentOpts = null
}

/**
 * 获取当前连接状态
 */
export function getConnectionState() {
  return connectionState
}

/**
 * 连接单品种行情 WebSocket
 * 例: connectSymbolWS('XAUUSD', { onQuote: (data) => {...} })
 */
export function connectSymbolWS(symbol, opts = {}) {
  const url = `${WS_BASE}/ftx/market/ws/${symbol}`
  let socket = null
  let symbolHeartbeat = null
  let symbolPongCheck = null
  let symbolLastPong = 0

  try {
    socket = new WebSocket(url)
  } catch (e) {
    console.error(`[WS] ${symbol} 创建连接失败:`, e)
    return null
  }

  socket.onopen = () => {
    opts.onOpen?.()
    symbolLastPong = Date.now()

    // 心跳
    symbolHeartbeat = setInterval(() => {
      if (socket && socket.readyState === WebSocket.OPEN) {
        try {
          socket.send(JSON.stringify({ action: 'ping' }))
        } catch (e) { /* heartbeat ping send failure, will be caught by pong timeout */ }
      } else {
        clearInterval(symbolHeartbeat)
      }
    }, HEARTBEAT_INTERVAL)

    // pong 超时检测
    symbolPongCheck = setInterval(() => {
      if (!socket || socket.readyState !== WebSocket.OPEN) return
      const elapsed = Date.now() - symbolLastPong
      if (elapsed > PONG_TIMEOUT) {
        console.warn(`[WS] ${symbol} pong 超时，重连`)
        try { socket.close() } catch (e) { /* close on pong timeout, ignore error */ }
      }
    }, 10000)
  }

  socket.onclose = () => {
    clearInterval(symbolHeartbeat)
    clearInterval(symbolPongCheck)
    opts.onClose?.()
  }

  socket.onerror = (e) => console.error(`[WS] ${symbol} 连接错误:`, e)

  socket.onmessage = (event) => {
    try {
      const msg = JSON.parse(event.data)
      if (msg.type === 'quote') {
        opts.onQuote?.(msg.data)
      } else if (msg.type === 'pong') {
        symbolLastPong = Date.now()
      }
    } catch (e) {
      console.warn(`[WS] ${symbol} 消息解析失败:`, e)
    }
  }

  return socket
}

// ============ 页面可见性感知 ============
// 切回前台时检查连接状态，断开则立即重连
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    // 页面恢复可见，检查连接
    if (!intentionallyClosed && currentOpts) {
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        console.log('[WS] 页面恢复可见，检查连接...')
        reconnectDelay = RECONNECT_BASE_DELAY  // 重置退避
        _doConnect()
      }
    }
  }
})

// ============ 网络状态感知 ============
// 网络恢复时立即重连
window.addEventListener('online', () => {
  if (!intentionallyClosed && currentOpts) {
    console.log('[WS] 网络恢复，立即重连...')
    reconnectDelay = RECONNECT_BASE_DELAY
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
    _doConnect()
  }
})
